import React, { useState, useEffect, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, ButtonGroup, Button } from 'reactstrap';
import { noop } from 'lodash';
import { VehicleEntities } from 'client/data/models/vehicle';
import { VehicleVinModel, buildStylesBasicPathFromVin } from 'client/data/models/vehicle-vin';
import { VehicleDropdowns } from 'site-modules/shared/components/vehicle-dropdowns/vehicle-dropdowns';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import {
  ALLOWED_PUB_STATES,
  APPRAISAL_PAGE_CREATIVE_ID,
  APPRAISER_STEP_BUTTON_TRACKING,
  LOCATION_AND_STYLE,
  COLORS_AND_OPTIONS,
  CONTINUE_BUTTON_CLASSNAME,
  COLORS_AND_OPTIONS_STEP,
} from 'site-modules/shared/constants/appraisal/appraisal';
import classNames from 'classnames';
import {
  MAKE_INPUT,
  MODEL_INPUT,
  YEAR_INPUT,
  STYLE_INPUT,
} from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { ZipField, THEME } from 'site-modules/shared/components/appraisal/zip-field/zip-field';
import { AppraiseByTabsHeaderItem } from 'site-modules/shared/components/appraisal/appraisal-tabs/appraise-by-tabs-header-item';
import { AppraisalTabLink } from 'site-modules/shared/components/appraisal/appraisal-tabs/appraisal-tab-link/appraisal-tab-link';
import {
  MMY_TAB_ID,
  TABS,
  VIN_TAB,
  VIN_TAB_ID,
  LICENSE_PLATE_TAB_ID,
  TAB_LINKS_ORDER_BY,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getStepCtaAriaLabel } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-utils';
import { LicensePlateAndVinFormAsync } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/license-plate-and-vin-form-async';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { fireSelectVinPixel } from 'site-modules/shared/utils/appraisal/common-utils';

import { useLocationAndStyleStepStorage, useStepLoadingHandler } from './location-and-style-step-hooks';
import { VEHICLE_DROPDOWNS_TRACKING } from './location-and-style-step-constants';

import './location-and-style-step.scss';

export const LocationAndStyleStepWithVinLpTabsUI = ({
  isZipCodeValid,
  onZipCodeChange,
  onDropdownsSelect,
  updateStepsState,
  selectStyle,
  isMobile,
  zipCode,
  vin,
  estimatedVehicle,
  vehicle,
  errorMap,
  vinStyles,
  isStepActive,
  updateErrorMapAndScrollToInvalid = noop,
  onPartnerOfferVinEligibilityUpdate,
  onActiveInventoryCheck,
  updateVinAndContinue,
  clearVin,
  changeStep,
  isLoadingColorsAndOptions,
  isAllowedToPredefineVin,
  queryVin,
  checkVinMismatchError,
  nextStepTitle,
  stepCounter,
  displayTipForTruckType,
  styleIds,
  params,
  isEmo2933Chal1,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const isLoaded = useStepLoadingHandler();
  const [isHiddenVinLicensePlateBtn, setIsHiddenVinLicensePlateBtn] = useState(false);
  const [activeTabId, setActiveTabId] = useState(MMY_TAB_ID);
  const [wasLPAndVinFormMountedBefore, setWasLPAndVinFormMountedBefore] = useState(false);
  const [isLPAndVinFormReady, setIsLPAndVinFormReady] = useState(false);

  const isMmyTabActive = activeTabId === MMY_TAB_ID;
  const shouldShowLPAndVinForm = activeTabId !== MMY_TAB_ID;
  const shouldMountLPAndVinForm = shouldShowLPAndVinForm || !isStepActive;

  const storage = useLocationAndStyleStepStorage();

  useEffect(() => {
    if (shouldMountLPAndVinForm) {
      setWasLPAndVinFormMountedBefore(true);
    }
  }, [shouldMountLPAndVinForm]);

  const isStylesFound = !!vinStyles?.length;
  const isVinStylesLoaded = vinStyles !== null;

  useEffect(() => {
    setIsHiddenVinLicensePlateBtn(isMmyTabActive || isVinStylesLoaded);
  }, [isMmyTabActive, isVinStylesLoaded]);

  const onSelect = useCallback(
    (make, model, year, style, isSetVehicleDataForExtendedReport) => {
      setSelectedVehicle({ make, model, year: year?.year, id: style?.id });
      onDropdownsSelect(make, model, year, style, isSetVehicleDataForExtendedReport, () => {
        if (vin) {
          checkVinMismatchError(vinStyles);
        }
      });
    },
    [checkVinMismatchError, onDropdownsSelect, vin, vinStyles]
  );

  const onLPAndVinFormReady = useCallback(() => setIsLPAndVinFormReady(true), []);

  const showVinLpBtn = useCallback(() => {
    setIsHiddenVinLicensePlateBtn(false);
  }, []);

  const setActiveTab = useCallback(
    tabId =>
      delayHeavyTask(() => {
        setActiveTabId(tabId);
      }),
    []
  );

  const setVinTabActive = useCallback(() => {
    setActiveTab(VIN_TAB_ID);
  }, [setActiveTab]);

  const setMmyTabActive = useCallback(() => {
    setActiveTab(MMY_TAB_ID);
  }, [setActiveTab]);

  const getStyleIds = useCallback(
    vinStylesParam => {
      if (styleIds) return styleIds;
      return vinStylesParam.map(vinStyle => vinStyle.styleId).join();
    },
    [styleIds]
  );

  const handleTabLinkClick = useCallback(
    event => {
      const tabId = event.currentTarget.dataset.tabId;
      setActiveTab(tabId);
    },
    [setActiveTab]
  );

  const getTabLinkText = useCallback(
    tabLink => {
      const { id, label } = tabLink;
      return activeTabId === id ? label : <AppraisalTabLink onClick={handleTabLinkClick} tabLink={tabLink} />;
    },
    [activeTabId, handleTabLinkClick]
  );

  const styleDropdownId = `style-sel-${useId()}`;
  const zipId = `zip-${useId()}`;

  const trackVinEntry = useCallback(newVin => {
    fireSelectVinPixel(newVin, 'edm-entry-appraisal-step-1');
  }, []);

  const handleOnClick = useCallback(
    e => {
      const { currentTarget } = e;
      changeStep({ currentTarget });
    },
    [changeStep]
  );

  const withTabSpinner = shouldShowLPAndVinForm && !isLPAndVinFormReady;

  const seeValueCta = isLoaded ? 'See your car’s value' : 'Loading...';
  const continueCta = isLoaded ? 'Continue' : 'Loading...';
  const ariaLabel = getStepCtaAriaLabel(stepCounter, nextStepTitle);

  if (!isStepActive) return null;

  const vinStylesArray = vinStyles || [];
  const hasManyStyles = vinStylesArray.length > 1;
  const errorMake = errorMap.get(MAKE_INPUT);
  const errorModel = errorMap.get(MODEL_INPUT);
  const errorYear = errorMap.get(YEAR_INPUT);
  const errorStyle = errorMap.get(STYLE_INPUT);
  const hasVehicleData = !!estimatedVehicle;
  const { make, model, year } = vehicle;
  const hasOneStyle = vinStylesArray.length === 1;
  let styleName = '';
  if (hasOneStyle) {
    styleName = vinStylesArray[0].styleName;
  }
  const isRenderLPAndVinForm = shouldMountLPAndVinForm || wasLPAndVinFormMountedBefore;

  return (
    <div
      className={classNames('location-and-style-step', {
        'd-flex flex-column with-tab-spinner': withTabSpinner,
        'vin-tab': activeTabId === VIN_TAB_ID,
        'lp-tab': activeTabId === LICENSE_PLATE_TAB_ID,
      })}
    >
      <div className="location-and-style-step">
        <Row>
          <Col xs={12} className="px-0 mb-1">
            <ButtonGroup size="md" role="tablist">
              {TABS.DEFAULT.map(({ id, label }, index) => (
                <AppraiseByTabsHeaderItem
                  key={id}
                  label={label}
                  tabId={id}
                  isActive={id === activeTabId}
                  onBtnClick={setActiveTab}
                  isFirstItem={index === 0}
                  isAppraisalValuePage
                />
              ))}
            </ButtonGroup>
          </Col>
        </Row>
        {!isMmyTabActive && (
          <Row className={classNames({ 'd-none': withTabSpinner })}>
            <Col xs={12} md={6} className="px-0">
              <ZipField
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-1 mb-0"
                errorMap={errorMap}
                disabled={!isLoaded}
                zipWrapperClassName="mb-1"
              />
            </Col>
          </Row>
        )}
        <Row className={classNames({ 'd-none': withTabSpinner })}>
          <Col xs={12} md={activeTabId === VIN_TAB_ID ? 6 : 12} lg={activeTabId === VIN_TAB_ID ? 6 : 12}>
            {isRenderLPAndVinForm && (
              <LicensePlateAndVinFormAsync
                activeTabId={activeTabId}
                vehicle={vehicle}
                creativeId={APPRAISAL_PAGE_CREATIVE_ID}
                setActiveTab={setActiveTab}
                clearVin={clearVin}
                updateErrorMapAndScrollToInvalid={updateErrorMapAndScrollToInvalid}
                onPartnerOfferVinEligibilityUpdate={onPartnerOfferVinEligibilityUpdate}
                updateVinAndContinue={updateVinAndContinue}
                onActiveInventoryCheck={onActiveInventoryCheck}
                onValueChange={showVinLpBtn}
                useUniqId
                isMobile={isMobile}
                updateStepsState={updateStepsState}
                isSecondary
                hideVinLicensePlateBtn={isHiddenVinLicensePlateBtn}
                storage={storage}
                isVinDecoderUsed={isEmo2933Chal1}
                /* Vin Tab Props below */
                queryVin={queryVin}
                vin={vin}
                isVinFromDrawer={!!vin && !isAllowedToPredefineVin && vin !== queryVin}
                trackVinEntry={trackVinEntry}
                isStepActive={isStepActive}
                isLocationStyleStep
                isFullWidth
                vinFromDrawerSquishStyles={vinStylesArray}
                isAllowedToPredefineVin={isAllowedToPredefineVin}
                /* License Plate Tab Props below */
                vinTabLink={
                  <AppraisalTabLink classNames="text-underline" onClick={setVinTabActive} tabLink={VIN_TAB} />
                }
                onReady={onLPAndVinFormReady}
                params={params}
              />
            )}
            {isStylesFound && isHiddenVinLicensePlateBtn && !isMmyTabActive && (
              <Row className={classNames('size-14', { 'mb-1_5': !hasOneStyle })}>
                <Col xs={12} className="px-0">
                  <div className="fw-medium">
                    {year.year} {make.name} {model.name}
                  </div>
                  <div>{styleName}</div>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        <Row className={classNames({ 'd-none': withTabSpinner })}>
          <Col xs={12} className="px-0">
            <VehicleDropdowns
              tag="div"
              showAllSubmodels={hasVehicleData}
              hasModel
              hasYear
              hasStyle
              vehicle={selectedVehicle || estimatedVehicle}
              selectStyle={selectStyle}
              hasOptionTruncate={false}
              inputClasses="form-control pe-2 w-100 mmy-select input-content"
              makeLabel="Make"
              modelLabel="Model"
              yearLabel="Year"
              styleLabel="Style"
              labelClassName="small mb-0_25"
              errorMessageClassNames="mt-0_5"
              makeDropdownClassNames={classNames('has-right-padding mb-1 p-0 pe-md-0_75 pe-lg-1', {
                'd-none': !isMmyTabActive,
              })}
              modelDropdownClassNames={classNames('mb-1 p-0 ps-md-0_75 ps-lg-1', {
                'd-none': !isMmyTabActive,
              })}
              submodelDropdownClassNames={classNames('mb-1 p-0 ps-md-0_75 ps-lg-1', {
                'd-none': !isMmyTabActive,
              })}
              yearDropdownClassNames={classNames('has-right-padding mb-1 mb-md-0 p-0 pe-md-0_75 pe-lg-1', {
                'd-none': !isMmyTabActive,
              })}
              styleDropdownClassNames={classNames('p-0', {
                'd-none': !isMmyTabActive && ((isStylesFound && !hasManyStyles) || !isHiddenVinLicensePlateBtn),
                'mb-0 ps-lg-1 ps-md-0_75 pos-r style-dropdown-error-gutter': isMmyTabActive,
                'mt-1 mt-md-0 has-right-padding pe-lg-1 pe-md-0_75 col-12 col-md-6': !isMmyTabActive,
              })}
              buttonGroupClassNames="d-none"
              onDropdownsSelect={onSelect}
              errorMake={errorMake}
              errorModel={errorModel}
              errorYear={errorYear}
              errorStyle={errorStyle}
              pubStatesYears={ALLOWED_PUB_STATES}
              isMobile={isMobile}
              updateStepsState={updateStepsState}
              disabled={!isLoaded}
              enableSpinner
              styleIds={getStyleIds(vinStylesArray)}
              showFindStyleLink={isMmyTabActive}
              isStyleIdQueryParamEnabled={!vinStylesArray.length || vinStylesArray.length > 1}
              isStyledSelects
              styleDropdownId={styleDropdownId}
              displayTipForTruckType={displayTipForTruckType}
              required={{
                make: true,
                model: true,
                year: true,
                style: true,
              }}
              hasLiveRegions
              tracking={VEHICLE_DROPDOWNS_TRACKING}
              storage={storage}
              params={params}
              isVinDecoderUsed={isEmo2933Chal1}
            />
          </Col>
        </Row>
        {isMmyTabActive && !isMobile && (
          <Row className={classNames({ 'd-none': withTabSpinner })}>
            <Col xs={12} md={6} className="px-0 mt-1">
              <ZipField
                id={zipId}
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-1 mb-0 mmy-zip-code"
                errorMap={errorMap}
                disabled={!isLoaded}
              />
            </Col>
          </Row>
        )}
        {isMmyTabActive && isMobile && (
          <Row className={classNames({ 'd-none': withTabSpinner })}>
            <Col xs={4} md={6} className="px-0 mt-0_25 mt-md-3">
              <ZipField
                id={zipId}
                theme={THEME.LOCATION_ICON}
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-0_75 mb-0"
                errorMap={errorMap}
                disabled={!isLoaded}
                isMobile
                zipWrapperClassName="mb-0"
              />
            </Col>
            <Col xs={7} md={6} lg={4} className="px-0 mt-md-3 ms-auto ps-md-0_75 mt-1_25">
              <Button
                type="submit"
                onClick={handleOnClick}
                data-index={COLORS_AND_OPTIONS}
                data-tracking-id={APPRAISER_STEP_BUTTON_TRACKING[LOCATION_AND_STYLE]}
                color="success"
                className={classNames(
                  'heading-5 text-white py-0_5 form-button text-transform-none pos-r d-block w-100',
                  CONTINUE_BUTTON_CLASSNAME
                )}
                disabled={!isLoaded || isLoadingColorsAndOptions}
              >
                {isLoadingColorsAndOptions && (
                  <span className="pe-0_5">
                    <Spinner size={14} thickness={1} color="white" />
                  </span>
                )}
                Continue
              </Button>
            </Col>
          </Row>
        )}
      </div>
      {withTabSpinner && (
        <div
          className="tab-spinner-container d-flex justify-content-center align-items-center mb-3_5"
          data-testid="tab-spinner"
        >
          <Spinner size={80} thickness={4} />
        </div>
      )}

      {((isMmyTabActive && !isMobile) || (isHiddenVinLicensePlateBtn && (!isMmyTabActive || !isMobile))) && (
        <Row className={classNames('mt-2', { 'mt-md-2_5': isMmyTabActive, 'd-none': withTabSpinner })}>
          <Col xs={12} md={4} lg={5} xl={4} className="px-0">
            <Button
              type="submit"
              onClick={handleOnClick}
              data-index={COLORS_AND_OPTIONS}
              data-tracking-id={APPRAISER_STEP_BUTTON_TRACKING[LOCATION_AND_STYLE]}
              color="success"
              className={classNames(
                'heading-5 text-white py-0_5 form-button text-transform-none pos-r d-block w-100',
                CONTINUE_BUTTON_CLASSNAME
              )}
              disabled={!isLoaded || isLoadingColorsAndOptions}
              aria-label={ariaLabel}
            >
              {isLoadingColorsAndOptions && (
                <span className="pe-0_5">
                  <Spinner size={14} thickness={1} color="white" />
                </span>
              )}
              {isMmyTabActive ? continueCta : seeValueCta}
            </Button>
          </Col>
          {!isMmyTabActive && (
            <Col xs={12} md={4}>
              <Button
                type="submit"
                color="link"
                onClick={setMmyTabActive}
                className={classNames(
                  'heading-5 text-primary-darker py-0 text-transform-none d-block py-0_5 fw-normal mx-auto mx-md-0 mt-1 mt-md-0'
                )}
                disabled={!isLoaded}
              >
                Not my vehicle
              </Button>
            </Col>
          )}
        </Row>
      )}
      {!isMmyTabActive && (
        <Row className={classNames({ 'd-none': withTabSpinner })}>
          <Col
            xs={12}
            md={8}
            className="appraisal-tab-link-copy text-gray-dark size-14 mt-1_5 bg-gray-extra-light rounded p-1 tab-info"
            role="tablist"
          >
            <div className="fw-bold text-primary-darker">Did you know?</div>
            <div>
              <span>By entering your </span>
              {getTabLinkText(TAB_LINKS_ORDER_BY[activeTabId].FIRST)}
              {' or '}
              {getTabLinkText(TAB_LINKS_ORDER_BY[activeTabId].SECOND)}
              <span>, you’ll get a more accurate appraisal that may raise your car&apos;s value.</span>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

LocationAndStyleStepWithVinLpTabsUI.propTypes = {
  isZipCodeValid: PropTypes.bool.isRequired,
  onZipCodeChange: PropTypes.func.isRequired,
  onDropdownsSelect: PropTypes.func.isRequired,
  updateStepsState: PropTypes.func,
  selectStyle: PropTypes.bool,
  isMobile: PropTypes.bool,
  zipCode: PropTypes.string,
  vin: PropTypes.string,
  estimatedVehicle: VehicleEntities.StyleWithoutSubmodels,
  vehicle: PropTypes.shape({}),
  errorMap: PropTypes.objectOf(PropTypes.string),
  vinStyles: PropTypes.arrayOf(PropTypes.shape({})),
  isStepActive: PropTypes.bool,
  updateErrorMapAndScrollToInvalid: PropTypes.func,
  onPartnerOfferVinEligibilityUpdate: PropTypes.func,
  onActiveInventoryCheck: PropTypes.func,
  updateVinAndContinue: PropTypes.func,
  clearVin: PropTypes.func,
  changeStep: PropTypes.func,
  isLoadingColorsAndOptions: PropTypes.bool,
  isAllowedToPredefineVin: PropTypes.bool,
  queryVin: PropTypes.string,
  checkVinMismatchError: PropTypes.func,
  nextStepTitle: PropTypes.string,
  stepCounter: PropTypes.number,
  displayTipForTruckType: PropTypes.bool,
  styleIds: PropTypes.string,
  params: PropTypes.shape({
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
    styleId: PropTypes.string,
  }),
  isEmo2933Chal1: PropTypes.bool,
};

LocationAndStyleStepWithVinLpTabsUI.defaultProps = {
  zipCode: '',
  vin: '',
  estimatedVehicle: null,
  vehicle: {},
  selectStyle: false,
  isStepActive: false,
  isMobile: false,
  errorMap: new Map(),
  updateStepsState: noop,
  vinStyles: null,
  updateErrorMapAndScrollToInvalid: noop,
  onPartnerOfferVinEligibilityUpdate: noop,
  onActiveInventoryCheck: noop,
  updateVinAndContinue: noop,
  clearVin: noop,
  changeStep: noop,
  isLoadingColorsAndOptions: false,
  isAllowedToPredefineVin: true,
  queryVin: '',
  checkVinMismatchError: noop,
  nextStepTitle: COLORS_AND_OPTIONS_STEP.title(),
  stepCounter: 1,
  displayTipForTruckType: false,
  styleIds: '',
  params: {},
  isEmo2933Chal1: false,
};

export const stateToPropsConfig = {
  vinStyles: bindToPath(
    ({ vin, isEmo2933Chal1 }) => buildStylesBasicPathFromVin({ vin, isVinDecoderUsed: isEmo2933Chal1 }),
    VehicleVinModel,
    (data, { isEmo2933Chal1, vehicle }) => {
      const results = isEmo2933Chal1 ? data.results : data;
      if (results && results.length) {
        const { make, model, year } = vehicle;
        const result = results[0];
        const isMakeEqual = result?.makeName?.toLowerCase() === make?.name?.toLowerCase();
        const isModelEqual = result?.modelName?.toLowerCase() === model?.name?.toLowerCase();
        const isYearEqual = result?.year?.toString() === year?.year?.toString();
        // Don't return any styles if they do not match the currently selected vehicle
        if (!(isMakeEqual && isModelEqual && isYearEqual)) return [];
      }

      return results;
    }
  ),
};

export const LocationAndStyleStepWithVinLpTabs = connectToModel(
  LocationAndStyleStepWithVinLpTabsUI,
  stateToPropsConfig
);
